/**
 * lib/api/archive.js — reads + writes for lbs_bookings_archive.
 *
 * Reads use the browser's Supabase client — client-scoped RLS
 * (`archive_read_tenant` policy from migration 102) filters to the
 * signed-in user's tenants automatically. No service-role needed.
 *
 * Writes (bulk import + cold-freeze) go through the backend router
 * `/api/admin/archive/*` because they require the service_role.
 */
import { supabase } from "@/lib/supabaseClient";
import { authHeaders } from "./_shared";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Server-paginated list of archive rows for the browser page.
 * @param {object} f — filter set
 * @param {string} f.clientId       required (except for super_admin)
 * @param {string} f.dateFrom       ISO date, optional
 * @param {string} f.dateTo         ISO date, optional
 * @param {string} f.status         'completed' | 'cancelled' | 'rejected' | 'no_show' | 'all'
 * @param {string} f.driver         fuzzy match on driver_name
 * @param {string} f.company        fuzzy match on sender OR receiver company
 * @param {string} f.reference      fuzzy match on reference_number
 * @param {string} f.direction      'inbound' | 'outbound' | 'all'
 * @param {string} f.legacySource   'booked_scheduler' | 'auto_archive' | 'all'
 * @param {number} limit            default 100
 * @param {number} offset           default 0
 */
export async function listArchive(f = {}, { limit = 100, offset = 0 } = {}) {
  let q = supabase
    .from("lbs_bookings_archive")
    .select(
      "id, client_id, site_id, reference_number, scheduled_start, scheduled_end, " +
      "actual_start, actual_end, status, direction, sender_company, receiver_company, " +
      "driver_name, driver_phone, driver_email, vehicle_type, vehicle_rego, " +
      "pallets_in, pallets_out, legacy_source, archived_at",
      { count: "exact" }
    )
    .order("scheduled_start", { ascending: false, nullsFirst: false })
    .range(offset, offset + limit - 1);

  if (f.clientId)               q = q.eq("client_id", f.clientId);
  if (f.dateFrom)               q = q.gte("scheduled_start", f.dateFrom);
  if (f.dateTo)                 q = q.lte("scheduled_start", f.dateTo);
  if (f.status && f.status !== "all")          q = q.eq("status", f.status);
  if (f.direction && f.direction !== "all")    q = q.eq("direction", f.direction);
  if (f.legacySource && f.legacySource !== "all") {
    if (f.legacySource === "auto_archive") q = q.is("legacy_source", null);
    else                                    q = q.eq("legacy_source", f.legacySource);
  }
  if (f.driver)    q = q.ilike("driver_name", `%${f.driver}%`);
  if (f.reference) q = q.ilike("reference_number", `%${f.reference}%`);
  if (f.company) {
    q = q.or(`sender_company.ilike.%${f.company}%,receiver_company.ilike.%${f.company}%`);
  }

  return q;
}

/** Fast aggregate for the page header + potential future health pill. */
export async function archiveStats({ clientId } = {}) {
  return supabase.rpc("lbs_archive_stats", { p_client_id: clientId || null });
}

/** Bulk-import a batch of flat rows (service_role only via backend). */
export async function bulkImportArchive(rows, { legacySource = "csv_import" } = {}) {
  const r = await fetch(`${API}/api/admin/archive/import`, {
    method: "POST",
    headers: await authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ rows, legacy_source: legacySource }),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    return { error: { message: body.detail || `Import failed (${r.status})` } };
  }
  return { data: await r.json() };
}

/** Manually trigger the cold-freeze pass (nightly cron entry point too). */
export async function coldFreezeArchive({ clientId = null, olderThanYears = 5 } = {}) {
  const r = await fetch(`${API}/api/admin/archive/cold-freeze`, {
    method: "POST",
    headers: await authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ client_id: clientId, older_than_years: olderThanYears }),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    return { error: { message: body.detail || `Cold freeze failed (${r.status})` } };
  }
  return { data: await r.json() };
}
